import React from 'react';

import { LUT_GROUPS, css, lutColor, lutLabel } from '../Viewer/lut';
import { COLLAGEN_DEFAULTS, orientationColor } from '../Viewer/collagen';
import { CardTitle, LutRange, Slider } from './ChannelCard';

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Légende de l'orientation : un trait par direction (0° horizontale, 90° verticale), dans sa couleur.
export function OrientationLegend() {
  const angles = [0, 30, 60, 90, 120, 150];
  return (
    <svg width="100%" height="30" viewBox="0 0 240 30" aria-label="Orientation colors">
      {angles.map((a, i) => {
        const cx = 20 + i * 40;
        const rad = (a * Math.PI) / 180;
        const [dx, dy] = [9 * Math.cos(rad), -9 * Math.sin(rad)];  // angle compté vers le haut, comme un rapporteur
        return (
          <g key={a}>
            <line x1={cx - dx} y1={13 - dy} x2={cx + dx} y2={13 + dy} stroke={css(orientationColor(2 * a, 1, 1))}
              strokeWidth="3" strokeLinecap="round" />
            <text x={cx} y={29} fill="currentColor" fontSize="8" textAnchor="middle">{a}°</text>
          </g>
        );
      })}
    </svg>
  );
}

// Canal « Collagène : orientation » (src/components/Viewer/collagen.js), présenté comme une modalité : interrupteur
// (exclusif), canal SHG, affichage (orientation, alignement ou aucune carte), vecteurs (couleur, longueur, épaisseur),
// niveau d'analyse, échelle σ, seuil, opacité, LUT ;
// chiffres (cohérence moyenne, orientation dominante, dispersion). cell : px pleine résolution par px d'analyse.
export default function CollagenCard({
  channels, c, settings, stats, cell, otsuThreshold, computing, active, onChange, onToggle, collapsed, onCollapse, label, onRename,
}) {
  const threshold = settings.threshold ?? otsuThreshold ?? 0.2;
  const gradient = `linear-gradient(to right, ${Array.from({ length: 17 }, (_, i) => css(lutColor(settings.lut, [255, 255, 255], false, i / 16, settings.lutRange))).join(', ')})`;
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch collagen" title="Computed channel: collagen orientation" />
        <CardTitle name={label || 'Collagen fiber orientation'} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={e => onToggle(e.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            SHG structure tensor: fiber orientation and coherence (0: fibers in all directions, 1: parallel).
          </p>
          <div className="compare-row">
            <span className="compare-label">SHG</span>
            <select value={c?.id || ''} aria-label="SHG channel" onChange={e => onChange({ c: e.target.value })}>
              {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
            </select>
          </div>
          <div className="compare-row">
            <span className="compare-label">Display</span>
            <select value={settings.display} aria-label="Orientation display" onChange={e => onChange({ display: e.target.value })}>
              <option value="orientation">Orientation (color = angle, saturation = alignment)</option>
              <option value="coherence">Alignment (coherence)</option>
              <option value="none">No map (SHG only)</option>
            </select>
          </div>
          {settings.display === 'coherence' && (
            <>
              <div className="compare-row">
                <span className="compare-label">LUT</span>
                <select value={settings.lut} aria-label="Alignment LUT" onChange={e => onChange({ lut: e.target.value })}>
                  {LUT_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.ids.map(id => <option key={id} value={id}>{lutLabel(id)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <LutRange lut={settings.lut} range={settings.lutRange} onChange={r => onChange({ lutRange: r })} />
              <div className="lut-bar" style={{ background: gradient }} />
              <div className="range-values"><span>0 (all directions)</span><span>1 (parallel)</span></div>
            </>
          )}
          {(settings.display === 'orientation' || (settings.vectors && settings.vecColor === 'orientation')) && <OrientationLegend />}
          <div className="channel-options">
            <label className={`chip${settings.vectors ? ' checked' : ''}`}
              title="One line per block, along the fibers; density follows the zoom">
              <input type="checkbox" checked={!!settings.vectors} onChange={e => onChange({ vectors: e.target.checked })} />
              Vectors
            </label>
          </div>
          {settings.vectors && (
            <>
              <div className="compare-row">
                <span className="compare-label">Lines</span>
                <select value={settings.vecColor} aria-label="Vector color" onChange={e => onChange({ vecColor: e.target.value })}>
                  <option value="white">White</option>
                  <option value="orientation">Angle color</option>
                </select>
                <select value={settings.vecLength} aria-label="Vector length" onChange={e => onChange({ vecLength: e.target.value })}>
                  <option value="coherence">Length = alignment</option>
                  <option value="fixed">Fixed length</option>
                </select>
              </div>
              <Slider label="Line width" value={settings.vecWidth} min={0.5} max={4} step={0.5}
                reset={COLLAGEN_DEFAULTS.vecWidth} format={v => `${num(v, 1)} px`} onChange={v => onChange({ vecWidth: v })} />
            </>
          )}
          <div className="compare-row">
            <span className="compare-label">Analysis</span>
            <select value={settings.detail} aria-label="Analysis level" onChange={e => onChange({ detail: e.target.value })}>
              <option value="standard">Standard (up to 5 Mpx)</option>
              <option value="fin">Fine (up to 15 Mpx, slower)</option>
            </select>
          </div>
          <Slider label={`Analysis scale (σ${cell ? `, ≈ ${Math.round(settings.sigma * cell)} full-resolution px` : ''})`}
            value={settings.sigma} min={1} max={12} step={0.5} reset={COLLAGEN_DEFAULTS.sigma}
            format={v => `σ ${num(v, 1)} px`} onChange={v => onChange({ sigma: v })} />
          <Slider label="Collagen threshold (normalized SHG; Otsu by default)" value={threshold} max={1} step={0.01}
            reset={otsuThreshold ?? 0.2} format={v => num(v)} onChange={v => onChange({ threshold: v })} />
          {settings.display !== 'none' && (
            <Slider label="Map opacity" value={settings.opacity} max={1} reset={COLLAGEN_DEFAULTS.opacity}
              onChange={v => onChange({ opacity: v })} />
          )}
          <p className="viewer-note" style={{ margin: 0 }}>
            {computing ? 'Computing…' : stats
              ? `Collagen: ${num(stats.area * 100, 1)}% of the image · mean coherence ${num(stats.coherence)} · dominant orientation ${Math.round(stats.angle)}° · dispersion ${num(stats.dispersion)}`
              : 'No collagen above the threshold.'}
          </p>
        </div>
      )}
    </div>
  );
}
